import React, { useContext, useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import styles from "./PlaceDetails.module.scss";
import PlaceBottomPanel from "../PlaceBottomPanel/PlaceBottomPanel.jsx";
import BackToMap from "../BackToMap/BackToMap.jsx";
import PlaceGallery from "../PlaceGallery/PlaceGallery.jsx";
import AudioPlayer from "../AudioPlayer/AudioPlayer.jsx";
import PlaceInfo from "../PlaceInfo/PlaceInfo.jsx";
import ViewARButton from "../ViewARButton/ViewARButton.jsx";
import { LanguageContext } from "../../contexts/LanguageContext";
import { VoiceContext } from "../../contexts/VoiceContext";
import { useWebARApp } from "../../contexts/WebARAppContext";
import { STORAGE_KEYS } from "../../constants/storage";
import localforage from "localforage";
import pinsData from "../../pins";

const PlaceDetails = () => {
  const { name } = useParams();
  const { localizedData, language } = useContext(LanguageContext);
  const { showAR } = useWebARApp();
  const placeData = localizedData?.[name];
  const bottomPanelContentRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const { voiceData } = useContext(VoiceContext);
  const [cachedAudioSrc, setCachedAudioSrc] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(true);

  const pinData = pinsData.find((pin) => pin.name === name);

  useEffect(() => {
    const loadGalleryData = async () => {
      try {
        const galleryData = await localforage.getItem(STORAGE_KEYS.GALLERY);
        if (galleryData?.[name]?.galleryImages) {
          setGalleryImages(galleryData[name].galleryImages);
        } else {
          setGalleryImages([]);
        }
      } catch (error) {
        console.error("Error loading gallery data from cache:", error);
        setGalleryImages([]);
      }
    };

    loadGalleryData();
  }, [name]);

  useEffect(() => {
    const loadCachedAudio = async () => {
      try {
        setIsAudioLoading(true);
        const audioSrc = voiceData?.[name]?.audioSrc;

        if (audioSrc) {
          const cachedAudio = await localforage.getItem(
            `${STORAGE_KEYS.VOICE_FILES}_${audioSrc}`
          );

          if (cachedAudio) {
            const audioUrl = URL.createObjectURL(cachedAudio);
            setCachedAudioSrc(audioUrl);
          } else {
            setCachedAudioSrc(audioSrc);
          }
        }
      } catch (error) {
        console.error("Error loading audio from cache:", error);
        setCachedAudioSrc(voiceData?.[name]?.audioSrc);
      } finally {
        setIsAudioLoading(false);
      }
    };

    if (voiceData?.[name]?.audioSrc) {
      loadCachedAudio();
    }

    // Clean up URLs when component unmounts
    return () => {
      if (cachedAudioSrc?.startsWith("blob:")) {
        URL.revokeObjectURL(cachedAudioSrc);
      }
    };
  }, [name, voiceData]);

  if (!placeData) {
    return <div>Loading...</div>;
  }

  const handleViewAR = async () => {
    if (audioPlayerRef.current && audioPlayerRef.current.isPlaying) {
      audioPlayerRef.current.togglePlay();
    }

    if (pinData?.arName) {
      await showAR(pinData.arName, language);
    } else {
      console.error("AR name is not defined for this pin.");
    }
  };

  let goTo = "/temple-map";
  if (name === "what_is_manpukuji_temple") {
    goTo = "/places/what_is_zen";
  }

  return (
    <div className={styles.placeDetails}>
      <PlaceGallery images={galleryImages} />
      <PlaceBottomPanel>
        {isAudioLoading ? (
          <div>Loading audio...</div>
        ) : (
          <AudioPlayer ref={audioPlayerRef} audioSrc={cachedAudioSrc} />
        )}
        <div className={styles.bottomPanelContent} ref={bottomPanelContentRef}>
          <PlaceInfo
            title={placeData.title}
            description={placeData.description}
          />
        </div>
      </PlaceBottomPanel>
      <BackToMap
        isContinue={
          name === "what_is_manpukuji_temple" || name === "what_is_zen"
        }
        goTo={goTo}
      />
      {pinData?.ar && <ViewARButton onClick={handleViewAR} />}
    </div>
  );
};

export default PlaceDetails;
